import { supabase } from '@/integrations/supabase/client';
import { agentsClient } from '@/integrations/clients';
import { OpenAIThreadsService } from '@/hooks/ai-agents/services/openai/threads.service';
import { AgentResponse } from '@/integrations/openai/types';
import { logInfo, logError, LogCategory } from '@/utils/logger';

export interface AssistantConfig {
  id: string;
  name: string;
  openai_assistant_id: string;
  profile_picture_url?: string;
  priority?: number;
  auto_respond?: boolean;
  response_delay_minutes?: number;
}

export interface CommunityAIResponse {
  success: boolean;
  responses: Array<{
    assistantId: string;
    assistantName: string;
    response?: AgentResponse;
    error?: string;
  }>;
}

/**
 * Service for Community AI integration
 * Uses existing Agents Edge Function with openaiThreadId parameter to bypass database lookup
 */
export class CommunityAIService {
  /**
   * Create a new OpenAI thread for a community discussion
   * Reuses existing OpenAIThreadsService
   */
  static async createCommunityAIThread(executeAIOperation?: (operation: () => Promise<any>, options?: any) => Promise<any>): Promise<string> {
    logInfo('Creating OpenAI thread for community discussion', { 
      category: LogCategory.API 
    });

    try {
      const openaiThreadId = await OpenAIThreadsService.createOpenAIThread(executeAIOperation);
      
      logInfo('Community OpenAI thread created successfully', { 
        category: LogCategory.API,
        data: { openaiThreadId }
      });

      return openaiThreadId;
    } catch (error: any) {
      logError('Failed to create community OpenAI thread', {
        category: LogCategory.API,
        data: { error: error.message }
      });
      throw error;
    }
  }

  /**
   * Select an agent using weighted random selection based on priority
   * Priority 1 = 40% chance, Priority 2 = 30% chance, Priority 3 = 20% chance, etc.
   * Formula: weight = 5 - priority (so priority 1 gets weight 4, priority 2 gets weight 3, etc.)
   */
  static selectAgentByWeight(agents: AssistantConfig[]): AssistantConfig | null {
    if (agents.length === 0) return null;
    
    // Calculate weights: higher priority (lower number) = higher weight
    const agentsWithWeights = agents.map(agent => ({
      agent,
      weight: Math.max(1, 5 - (agent.priority || 1)) // Priority 1->4, Priority 2->3, Priority 3->2, etc.
    }));
    
    // Calculate total weight
    const totalWeight = agentsWithWeights.reduce((sum, item) => sum + item.weight, 0);
    
    // Generate random number between 0 and totalWeight
    const randomNum = Math.random() * totalWeight;
    
    // Find the selected agent
    let currentWeight = 0;
    for (const item of agentsWithWeights) {
      currentWeight += item.weight;
      if (randomNum <= currentWeight) {
        logInfo('Agent selected by weighted random', {
          category: LogCategory.API,
          data: {
            selectedAgent: item.agent.name,
            selectedPriority: item.agent.priority,
            selectedWeight: item.weight,
            totalWeight,
            randomNum: Math.round(randomNum * 100) / 100,
            allWeights: agentsWithWeights.map(a => ({ 
              name: a.agent.name, 
              priority: a.agent.priority, 
              weight: a.weight 
            }))
          }
        });
        return item.agent;
      }
    }
    
    // Fallback (should never happen)
    return agentsWithWeights[0].agent;
  }

  /**
   * Get AI agents configured for a specific category
   */
  static async getAgentsForCategory(category: string): Promise<AssistantConfig[]> {
    logInfo('Getting AI agents for category', { 
      category: LogCategory.DATABASE,
      data: { category }
    });

    try {
      // Get category configuration
      // Note: ai_agents column may not exist yet - handle gracefully
      const { data: categoryConfig, error: categoryError } = await supabase
        .from('community_discussion_categories')
        .select('*')
        .eq('name', category)
        .single();

      if (categoryError) {
        logError('Failed to get category configuration', {
          category: LogCategory.DATABASE,
          data: { error: categoryError.message, category }
        });
        // Return empty array if category not found or no AI agents configured
        return [];
      }

      // Safely access ai_agents property - it may not exist yet
      const agentConfigs = ((categoryConfig as any)?.ai_agents as AssistantConfig[]) || [];
      
      if (agentConfigs.length === 0) {
        logInfo('No AI agents configured for category', { 
          category: LogCategory.DATABASE,
          data: { category }
        });
        return [];
      }

      // Get actual assistant data from agent_assistants table
      const agentIds = agentConfigs.map(config => config.id);
      const { data: assistants, error: assistantsError } = await supabase
        .from('agent_assistants')
        .select('id, name, openai_assistant_id, profile_picture_url')
        .in('id', agentIds)
        .eq('is_active', true);

      if (assistantsError) {
        logError('Failed to get assistant data', {
          category: LogCategory.DATABASE,
          data: { error: assistantsError.message, agentIds }
        });
        return [];
      }

      // Merge category configuration with assistant data
      const mergedAgents = assistants?.map(assistant => {
        const config = agentConfigs.find(c => c.id === assistant.id);
        return {
          ...assistant,
          priority: config?.priority || 0,
          auto_respond: config?.auto_respond !== false, // Default to true
          response_delay_minutes: config?.response_delay_minutes || 0
        };
      }) || [];

      // Sort by priority (higher priority first)
      mergedAgents.sort((a, b) => (b.priority || 0) - (a.priority || 0));

      logInfo('Retrieved AI agents for category', { 
        category: LogCategory.DATABASE,
        data: { category, agentCount: mergedAgents.length }
      });

      return mergedAgents;
    } catch (error: any) {
      logError('Failed to get agents for category', {
        category: LogCategory.DATABASE,
        data: { error: error.message, category }
      });
      return [];
    }
  }

  /**
   * Trigger AI response for a community discussion
   * Uses existing agentsClient with openaiThreadId to bypass database lookup
   */
  static async triggerAIResponse(
    postContent: string,
    discussionId: string,
    assistantId: string,
    openaiThreadId: string
  ): Promise<AgentResponse> {
    logInfo('Triggering AI response for community discussion', { 
      category: LogCategory.API,
      data: { 
        discussionId, 
        assistantId: assistantId.substring(0, 10) + '...',
        contentLength: postContent.length,
        openaiThreadId: openaiThreadId.substring(0, 10) + '...'
      }
    });

    try {
      // Enhance the message with community forum context
      const forumContextPrompt = this.buildForumContextPrompt(postContent);

      // Use existing agentsClient with openaiThreadId parameter
      // This bypasses the database lookup in the Agents Edge Function
      const aiResponse = await agentsClient.sendMessage(
        forumContextPrompt,                // Enhanced message with forum context
        discussionId,                      // threadId (for logging purposes)
        assistantId,                       // assistantId (OpenAI assistant ID)
        [],                               // messageHistory (empty for first post)
        openaiThreadId                    // openaiThreadId (bypasses database lookup)
      );

      logInfo('AI response generated successfully', { 
        category: LogCategory.API,
        data: { 
          discussionId,
          assistantId: assistantId.substring(0, 10) + '...',
          responseLength: aiResponse.response.length,
          tokensUsed: aiResponse.tokensUsed,
          processingTime: aiResponse.processingTime
        }
      });

      return aiResponse;
    } catch (error: any) {
      logError('Failed to generate AI response', {
        category: LogCategory.API,
        data: { 
          error: error.message, 
          discussionId, 
          assistantId: assistantId.substring(0, 10) + '...'
        }
      });
      throw error;
    }
  }

  /**
   * Build forum-specific context prompt for AI responses
   */
  private static buildForumContextPrompt(originalPostContent: string): string {
    return `You are a Community Manager for this entrepreneurship forum. Your role is to facilitate meaningful discussions, provide expert guidance, and foster a positive, engaging community environment.

COMMUNITY MANAGER GUIDELINES:
1. **Authority & Expertise**: Respond with the knowledge and perspective of an experienced community manager who understands entrepreneurship
3. **Moderate with Care**: Acknowledge good contributions, guide conversations toward valuable insights, and help members connect ideas
4. **Ask Strategic Questions**: Include 1-2 thoughtful questions that help the poster reflect deeper
5. **Keep It Punchy**: Responses should be concise, engaging, and impactful - avoid lengthy explanations
6. **Natural Conversation Flow**: Respond naturally as a skilled community manager would - don't follow a rigid checklist, just facilitate great discussion
6. **Show Off Your Personality**: You are extremely charismatic and hilarious. Turn up your personality to the MAX!

FORMAT YOUR RESPONSE:
- Use natural paragraph breaks (double line breaks)
- Use simple bullet points with - if listing items
- Keep sentences clear and accessible
- End with questions that invite community participation
- Aim for 2-4 short paragraphs maximum

USER'S POST:
${originalPostContent}

Respond as a skilled community manager helping to facilitate a valuable discussion for entrepreneurs. Keep it concise, punchy, and engaging.`;
  }

  /**
   * Save AI response as a community discussion reply
   */
  static async saveAIResponseAsReply(
    response: AgentResponse,
    discussionId: string,
    assistantData: AssistantConfig,
    originalUserId: string
  ): Promise<void> {
    logInfo('Saving AI response as community reply', { 
      category: LogCategory.DATABASE,
      data: { 
        discussionId, 
        assistantId: assistantData.id,
        responseLength: response.response.length
      }
    });

    try {
      const { error } = await supabase
        .from('community_discussion_replies')
        .insert({
          discussion_id: discussionId,
          content: response.response,
          user_id: originalUserId, // Use original post creator's user_id for RLS compliance
          is_ai_response: true,
          assistant_id: assistantData.id
        });

      if (error) {
        throw error;
      }

      // Update reply count in discussion - get current count and increment
      const { count } = await supabase
        .from('community_discussion_replies')
        .select('id', { count: 'exact' })
        .eq('discussion_id', discussionId);

      const { error: updateError } = await supabase
        .from('community_discussions')
        .update({ replies: count || 0 })
        .eq('id', discussionId);

      if (updateError) {
        logError('Failed to update discussion reply count', {
          category: LogCategory.DATABASE,
          data: { error: updateError.message, discussionId }
        });
        // Don't throw - reply was saved successfully
      }

      logInfo('AI response saved as community reply successfully', { 
        category: LogCategory.DATABASE,
        data: { discussionId, assistantId: assistantData.id }
      });
    } catch (error: any) {
      logError('Failed to save AI response as community reply', {
        category: LogCategory.DATABASE,
        data: { 
          error: error.message, 
          discussionId, 
          assistantId: assistantData.id 
        }
      });
      throw error;
    }
  }

  /**
   * Trigger AI responses for all agents configured for a category
   */
  static async triggerCommunityAI(
    postContent: string,
    discussionId: string,
    category: string,
    openaiThreadId: string,
    originalUserId: string
  ): Promise<CommunityAIResponse> {
    logInfo('Triggering community AI for discussion', { 
      category: LogCategory.API,
      data: { 
        discussionId, 
        category,
        contentLength: postContent.length,
        openaiThreadId: openaiThreadId.substring(0, 10) + '...'
      }
    });

    try {
      // Get agents for category
      const agents = await this.getAgentsForCategory(category);
      
      if (agents.length === 0) {
        logInfo('No AI agents configured for category', { 
          category: LogCategory.API,
          data: { category, discussionId }
        });
        return {
          success: true,
          responses: []
        };
      }

      // Filter agents that should auto-respond
      const autoRespondAgents = agents.filter(agent => agent.auto_respond !== false);
      
      // Weighted random selection - only pick ONE agent
      const selectedAgent = this.selectAgentByWeight(autoRespondAgents);
      
      if (!selectedAgent) {
        logInfo('No agent selected for response', { 
          category: LogCategory.API,
          data: { category, discussionId, availableAgents: autoRespondAgents.length }
        });
        return {
          success: true,
          responses: []
        };
      }
      
      logInfo('Generating AI response from selected agent', { 
        category: LogCategory.API,
        data: { 
          discussionId,
          selectedAgent: selectedAgent.name,
          selectedPriority: selectedAgent.priority,
          totalAgents: agents.length,
          autoRespondAgents: autoRespondAgents.length
        }
      });

      // Generate response for the selected agent only
      const responsePromises = [selectedAgent].map(async (agent) => {
        try {
          // Apply response delay if configured
          if (agent.response_delay_minutes && agent.response_delay_minutes > 0) {
            const delayMs = agent.response_delay_minutes * 60 * 1000;
            await new Promise(resolve => setTimeout(resolve, delayMs));
          }

          const aiResponse = await this.triggerAIResponse(
            postContent,
            discussionId,
            agent.openai_assistant_id,
            openaiThreadId
          );

          await this.saveAIResponseAsReply(aiResponse, discussionId, agent, originalUserId);

          return {
            assistantId: agent.id,
            assistantName: agent.name,
            response: aiResponse
          };
        } catch (error: any) {
          logError('AI response failed for agent', {
            category: LogCategory.API,
            data: { 
              error: error.message, 
              assistantId: agent.id,
              assistantName: agent.name,
              discussionId
            }
          });

          return {
            assistantId: agent.id,
            assistantName: agent.name,
            error: error.message
          };
        }
      });

      const responses = await Promise.all(responsePromises);
      
      const successCount = responses.filter(r => !r.error).length;
      const errorCount = responses.filter(r => r.error).length;

      logInfo('Community AI responses completed', { 
        category: LogCategory.API,
        data: { 
          discussionId,
          successCount,
          errorCount,
          totalResponses: responses.length
        }
      });

      return {
        success: errorCount === 0,
        responses
      };
    } catch (error: any) {
      logError('Failed to trigger community AI', {
        category: LogCategory.API,
        data: { 
          error: error.message, 
          discussionId, 
          category 
        }
      });
      throw error;
    }
  }
} 